import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Gestión de proyectos";
const heroImage = "https://net-basis.net/Bilder/project-management-planning-control-dach-market-entry.webp";
const heroAlt = "Un empresario crea un diagrama de flujo para gestionar un proyecto de entrada en el mercado.";
const heroClaimLine1 = "¡Perfectamente planificado!";
const heroClaimLine2 = "¡Controlado con éxito!";
const widget2 = "Su socio experimentado para un asesoramiento integral en gestión de proyectos y dirección directa de proyectos en la región DACH. Pilotamos proyectos complejos de entrada al mercado para fabricantes extranjeros de manera orientada a resultados, consciente de los riesgos y con una profunda experiencia local.";
const benefitsTitle = "Cómo se beneficia de nuestra experiencia en __gestión de proyectos__:";
const p1 = "NetBasis International apoya a sus socios fabricantes en su entrada al mercado, si lo desean, con un asesoramiento integral en gestión de proyectos y dirección directa de proyectos. Al fin y al cabo, el plan de querer estar presente en nuevos mercados es, en la mayoría de los casos, una decisión estratégica y, por tanto, sumamente importante. Y un fracaso debe evitarse a toda costa. Al mismo tiempo, un proyecto de este tipo suele ser extremadamente complejo y exige un mayor grado de conocimiento específico del mercado, competencia profesional y un enfoque específico que el que normalmente tiene a su disposición una empresa que no está presente sobre el terreno.";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Asunción fiable de la planificación, supervisión y dirección de un proyecto de entrada al mercado",
  "Ayuda estructurada en la concertación del alcance de los servicios, los plazos del proyecto, los costes, la financiación y los recursos necesarios",
  "Ejecución profesional de todo el control de proyectos local sobre el terreno",
  "Soporte fundamentado en la identificación, análisis, evaluación y gestión de los riesgos del proyecto",
  "Observación continua del progreso del proyecto y comparación precisa con la planificación inicial",
  "Supervisión permanente del cumplimiento de plazos, el avance de los contenidos y el control presupuestario",
  "Información continua sobre el proyecto y comunicación transparente con el socio fabricante",
  "Asistencia competente en todos los asuntos legales específicos del país y del mercado",
];

export default function GestionDeProyectosEsPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Gestión de Proyectos | NetBasis International"
        description="Dirección eficiente e implementación operativa de sus proyectos de entrada al mercado y ventas por expertos experimentados."
        ogUrl="https://net-basis.net/es/servicios/desarrollo-de-negocio/gestion-de-proyectos"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-start pt-[22%] md:pt-[10%] lg:pt-[12%] pl-[8%] md:pl-[10%] pointer-events-none">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-center leading-[1.15] md:leading-[1.5] animate-[trainingTextSlide_12s_ease-in-out_infinite]"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                  }}
                >
                  <span className="block">{heroClaimLine1}</span>
                  {heroClaimLine2 && <span className="block">{heroClaimLine2}</span>}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <h1 className="text-sm md:text-base font-semibold text-foreground-950 mb-4">{widget2}</h1>
              <p>
                {(() => {
                  const parts = p1.split(COMPANY_NAME);
                  return parts.map((part, i) => (
                    <span key={i}>
                      {part}
                      {i < parts.length - 1 && (
                        <strong className="text-primary-600">{COMPANY_NAME}</strong>
                      )}
                    </span>
                  ));
                })()}
              </p>
            </div>

            {/* Green heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i} className="mr-[0.15em]">{part}</em> : part
              )}
            </h2>

            {/* Benefits with green checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}